import { User } from "@/modules/working_hours/domain/User";
import { ClockRepo } from "@/modules/working_hours/domain/ClockRepo";

interface DayInfo {
  date: string | null;
  dayOfWeek: string | null;
  totalMinutes: number;
  clocks: Array<{ clockIn: string; clockOut: string }>;
  isEmpty: boolean;
}

interface MonthScreenData {
  users: User[];       
  monthInfo: DayInfo[][];
  currentMonth: number;
  currentYear: number;
}

export class LoadMonthClocksInfo {
  // get_day_clocks(arg0: string) {
  //   throw new Error("Method not implemented.");
  // }
  constructor(
    private screenData: MonthScreenData,
    private clockRepo: ClockRepo
  ) {}
    
  async execute(userId: number): Promise<void> {
    const user = this.screenData.users.find(u => u.id === userId);
    if (!user) return;
    
    const month = this.screenData.currentMonth;
    const year = this.screenData.currentYear;

    const startOfMonth = new Date(year, month - 1, 1); 
    const startOfNextMonth = new Date(year, month, 1); 
    
    const clocks = await this.clockRepo.find_by_criteria(
      [
        ['user_id', '==', user.id],  
        ['timestamp', '>=', startOfMonth],
        ['timestamp', '<', startOfNextMonth]
      ],
      undefined, 
      undefined,  
      undefined, 
      false      
    );
    
    const result = this.buildMonthStructure(month, year, clocks);
    this.screenData.monthInfo = result;
  }

  private buildMonthStructure(month: number, year: number, clocks: any[]): DayInfo[][] {
    const weeks: DayInfo[][] = [];
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    const dayNames = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    
    let currentWeek: DayInfo[] = [];
    
    for (let i = 0; i < firstDayOfMonth; i++) {
      currentWeek.push({
        date: null,
        dayOfWeek: null,
        totalMinutes: 0,
        clocks: [],
        isEmpty: true
      });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const dayOfWeek = new Date(year, month - 1, day).getDay();
      
      const dayClocks = clocks.filter(clock => {
        const t = clock.timestamp;
        const clockDate = `${t.getFullYear()}-${(t.getMonth() + 1).toString().padStart(2, '0')}-${t.getDate().toString().padStart(2, '0')}`;
        return clockDate === dateStr;
      });
      
      const clockPairs = this.pairClocksInOut(dayClocks);
      const totalMinutes = this.calculateTotalMinutes(clockPairs, dateStr);
      currentWeek.push({
      date: dateStr,                        
      dayOfWeek: dayNames[dayOfWeek],       
      totalMinutes: totalMinutes,             
      clocks: clockPairs,                    
      isEmpty: clockPairs.length === 0     
      });
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push({
          date: null,
          dayOfWeek: null,
          totalMinutes: 0,
          clocks: [],
          isEmpty: true
        });
      }
      weeks.push(currentWeek);
    }
    return weeks;
  }

  private pairClocksInOut(clocks: any[]): Array<{ clockIn: string; clockOut: string }> {
    // Ordenar clocks por timestamp
    const sortedClocks = clocks.sort((a, b) => a.timestamp - b.timestamp);

    const pairs = [];
    let startClock = null;

    for (const clock of sortedClocks) {
      // Si es inicio de trabajo (in o continue)
      if (clock.type === 'in' || clock.type === 'continue') {
        startClock = clock;
      }
      // Si es fin de trabajo (out o pause) y hay un inicio previo
      else if ((clock.type === 'out' || clock.type === 'pause') && startClock) {
        pairs.push({
          clockIn: startClock.timestamp.toTimeString().slice(0, 5),
          clockOut: clock.timestamp.toTimeString().slice(0, 5)
        });
        startClock = null;
      }
    }

    // Si quedó un startClock sin emparejar, agregarlo con "En curso"
    if (startClock) {
      pairs.push({
        clockIn: startClock.timestamp.toTimeString().slice(0, 5),
        clockOut: '...'
      });
    }

    return pairs;
  }

  private calculateTotalMinutes(clockPairs: Array<{ clockIn: string; clockOut: string }>, dateStr: string): number {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
    const isToday = dateStr === todayStr;

    return clockPairs.reduce((total, pair) => {
      if (pair.clockOut === '...') {
        if (!isToday) return total; 
        const [inHour, inMin] = pair.clockIn.split(':').map(Number);
        const inMinutes = inHour * 60 + inMin;
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        
        return total + Math.max(0, nowMinutes - inMinutes);
      }

      const [inHour, inMin] = pair.clockIn.split(':').map(Number);
      const [outHour, outMin] = pair.clockOut.split(':').map(Number);

      const inMinutes = inHour * 60 + inMin;
      const outMinutes = outHour * 60 + outMin;

      return total + (outMinutes - inMinutes);
    }, 0);
  }
}